import {
  Component,
  ErrorBoundary,
  JSX,
  Show,
  createEffect,
  on,
} from "solid-js";
import { Button } from "./Button";
import { ApiError } from "../../api/client";
import { describeAiError, quotaScope } from "../../utils/aiError";

export interface AiErrorBoundaryProps {
  /** The AI surface, named in the fallback copy — e.g. "AI suggestions". */
  surface: string;
  /** One sentence naming what the user can still do without this surface. */
  manualPath: string;
  /**
   * Given when the surface owns a way out of itself (a drawer, a modal). The
   * fallback then offers "Close", which calls this *and* resets — without the
   * reset, reopening the surface would paint the stale fallback.
   */
  onDismiss?: () => void;
  /**
   * A value that changes whenever the surface is closed or reopened. Needed
   * only where the surface stays mounted across that: the chat drawer is a
   * portal that slides off-canvas, and its header Close and the Cmd+J toggle
   * never reach `reset`, so without this the user reopens onto the fallback
   * they left behind.
   */
  resetKey?: unknown;
  children: JSX.Element;
}

type AiErrorKind = "quota" | "unavailable" | "unexpected";

const kindOf = (err: unknown): AiErrorKind => {
  if (quotaScope(err) !== null) return "quota";
  if (err instanceof ApiError && err.status === 503) return "unavailable";
  return "unexpected";
};

/**
 * Error boundary for a single AI surface.
 *
 * `RouteErrorBoundary` keeps a page failure off the shell, but it is not
 * enough for AI: half these surfaces (`ChatDrawer`, `ProposalReviewModal`)
 * are mounted by `AppShell` *outside* any route boundary, so a render throw
 * in one still reaches the app root and replaces the viewport. And where a
 * route boundary does cover the surface, losing the whole page also loses
 * the manual path the AI was only ever enriching.
 *
 * So each AI subtree gets one of these, placed to leave the manual path
 * mounted: the create form keeps its fields, the drawer keeps its header,
 * the modal keeps its close button. AI enriches, never blocks (FLOW-336).
 *
 * Retry is offered only where retrying can work. A quota refusal is paused
 * until the budget resets, so the fallback drops "Try again" and leads with
 * the manual path instead.
 */
export const AiErrorBoundary: Component<AiErrorBoundaryProps> = (props) => {
  return (
    <ErrorBoundary
      fallback={(err, reset) => {
        // Distinct from "Route render error:" and "Unhandled render error:" —
        // those mean a page or the whole app went down. This is one AI panel.
        console.error("AI surface render error:", err);
        // `defer` so the render that produced this fallback does not reset it.
        // If the cause is still there the throw simply recurs — one extra
        // render, no loop, because the key does not change again.
        createEffect(
          on(
            () => props.resetKey,
            () => reset(),
            { defer: true },
          ),
        );
        const kind = kindOf(err);
        return (
          <div
            role="alert"
            data-testid="ai-error-fallback"
            data-ai-error-kind={kind}
            class="space-y-2 rounded border border-signal-amber/25 bg-signal-amber-tint p-3 text-sm"
          >
            <p class="font-medium text-ink">
              {kind === "quota"
                ? "AI budget used up"
                : kind === "unavailable"
                  ? "AI temporarily unavailable"
                  : `${props.surface} stopped working`}
            </p>
            <p class="text-ink-2">
              {kind === "unexpected"
                ? "An unexpected error occurred. The rest of the app is unaffected."
                : describeAiError(err)}
            </p>
            <p class="text-ink-2">{props.manualPath}</p>
            <Show when={kind !== "quota" || props.onDismiss}>
              <div class="flex items-center gap-2 pt-1">
                <Show when={kind !== "quota"}>
                  <Button size="sm" variant="secondary" onClick={reset}>
                    Try again
                  </Button>
                </Show>
                <Show when={props.onDismiss}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      props.onDismiss?.();
                      reset();
                    }}
                  >
                    Close
                  </Button>
                </Show>
              </div>
            </Show>
          </div>
        );
      }}
    >
      {props.children}
    </ErrorBoundary>
  );
};
